/**
 * The Qualify tab — the rubric: what each answer is worth, what stops scoring
 * dead, and where a score becomes a verdict.
 *
 * Visual Reference: `design/screens/editor-qualify.html` — default · loading ·
 * empty (no rubric yet) · error · rules.
 *
 * THE RIGHT-HAND COLUMN IS NOT HERE. The export draws the calibration bench and
 * "Try an answer" beside these three cards; they are Stories 7.2 and 7.3, and
 * this tab grows into them the way `ConnectTab` grows into Story 9.1's routing.
 *
 * EVERY EDIT GOES THROUGH `edit()`. The store's patch stack is what gives this
 * tab undo, autosave and the save-state indicator without writing any of them —
 * the same reason the Build tab's inspector writes points through
 * `editor/rubric.ts` rather than mutating a draft it holds.
 *
 * NOTHING HERE COMPUTES A WARNING. `overWeightWarnings` lives in
 * `packages/schema/src/rubric.ts` beside the validation that already knows what
 * a criterion can award (Story 7.1's criterion, DECISION-031). A warning the UI
 * computed would be one the bench, the API and the exports each computed again,
 * and the fourth copy is the one that is wrong.
 *
 * THE EXTRACTION CARD IS ITS OWN FILE. `ExtractionCard` renders between
 * Criteria and Disqualifiers, where the export draws it, and keeps its rules in
 * `extraction.ts` — for the same reason this file keeps none of its own.
 *
 * Stories 7.1, 18.8 (rules) and 20.2 (the extraction card).
 */
import { useCallback, useEffect, useId, useState } from 'react'
import {
  overWeightWarnings,
  totalWeight,
  type Band,
  type Block,
  type Condition,
  type Criterion,
  type ExtractionField,
  type Funnel,
} from '@leadgible/schema'
import { ApiFailure, apiGet, apiPut } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Callout } from '../ui/Callout'
import { Select } from '../ui/Select'
import { Skeleton } from '../ui/Skeleton'
import { Bench } from './Bench'
import { IcpCard, ThresholdCard } from './EscalationCards'
import { ExtractionCard } from './ExtractionCard'
import { PointsPanel } from './ExtractedPoints'
import { Probe } from './Probe'
import { DRAWN as DERIVED } from './derived-copy'
import { addFitCriterion, hasIcpProfile, offersFit } from './derived-criterion'
import { addExtractedCriterion, unscoredFields } from './extracted-criterion'
import { readsOf, strandedKeys, strandedLine, type Reads } from './extracted-points'
import { COMPOSED, DRAWN, PROMPTED } from './extraction-copy'
import { scorableQuestions, setDisqualifying } from './rubric'
import { edit, useEditor } from './store'
import { scoreQuestion, setBandFields, setWeight } from './vocabulary'

/* ── the export's own copy, verbatim ────────────────────────────────────── */

const CRITERIA_ASIDE = 'What each answer is worth'
const DISQUALIFIERS_ASIDE = 'Hard gates — no score is calculated'
const BANDS_ASIDE = 'Where a score becomes a verdict'
const EMPTY_TITLE = 'No criteria yet'
const EMPTY_BODY =
  'Pick the answers that tell you a lead is worth calling, and give each one a weight out of 100.'
const BAND_NOTE =
  'Band colours carry meaning across every screen. You can override them, but nurture, review and hot keep their order.'
const SERVICE_AREA_LABEL = 'outside service area'

/**
 * The rules-only annotation under an extracted criterion, verbatim from the
 * export's `rules` capture.
 *
 * ONE STRING, NOT FOUR. The banner's wording varies by reason and arrives from
 * the API for that reason; this line does not, because the mockup draws exactly
 * one. Story 23.5 made `switched_off` reachable here, and the line stays as drawn
 * because it stays TRUE — an org whose Owner stopped AI spend is an org whose AI
 * layer is off — so no variant was invented; the banner above it names the cause.
 * `cap_reached` never reaches this tab (`services/ai/availability.ts` says why).
 */
const EXTRACTED_ZERO_NOTE = 'Scoring zero while the AI layer is off. Your weight is kept.'

/*
  NO LOCAL DEFAULT_WEIGHT. Every door in "Add a criterion" starts a criterion at
  one weight — until Story 20.3 this file kept a second 25, and a second copy is
  the one that stops agreeing. Since Story 27.2 the doors are the vocabulary's
  `set_criterion`, so the weight is the operation's own.
*/


/**
 * PRD §3.6 and RULE-021: the source says where a fact came FROM, never whether
 * it is good. `metadata` and `transcript` are M2/M5 and the export draws no
 * chip for either; the map is total so a later source cannot render as nothing.
 */
const SOURCE_LABEL: Record<Criterion['source'], string> = {
  answer_ref: 'Answer',
  extracted: 'Extracted',
  // Story 20.9 — the `points` capture's chip, in `Extracted`'s tint.
  derived: DERIVED.chip,
  metadata: 'Metadata',
  transcript: 'Transcript',
}

/**
 * The criteria a model's reading feeds: an extracted fact, or the ICP fit. They
 * share a chip tint (the capture's two styles are byte-identical), open to their
 * points, and score zero while the AI layer is off.
 */
const isRead = (criterion: Criterion): boolean =>
  criterion.source === 'extracted' || criterion.source === 'derived'

/** The ideal customer match's last row: no fit was measured, as the capture words it. */
const NOT_MEASURED = { label: DERIVED.notMeasured, name: DERIVED.scoreWhenNotMeasured }

/**
 * RULE-002 and PRD §1.9: "the band ladder contains **no red**. Nurture → review
 * → hot runs grey → amber → green. Red is reserved for errors and destructive
 * actions. A nurture lead is still an asset."
 *
 * So the swatch reads its colour from the TONE and nothing else, and
 * `--mdx-error` appears in this file exactly once — on Remove, which is a
 * destructive action and therefore the one place the rule allows it.
 */
const TONE_SWATCH: Record<Band['tone'], string> = {
  nurture: 'bg-mdx-nurture',
  review: 'bg-mdx-review',
  hot: 'bg-mdx-hot',
}

export function QualifyTab({ funnelId }: { funnelId: string }) {
  const funnel = useEditor((s) => s.funnel)
  const { me, org } = useShell()
  if (!funnel) return null

  /*
    ABSENT READS AS AVAILABLE, deliberately. `ai` is optional on the boot
    payload (an API older than Story 18.8 sends none), and a banner announcing
    an outage on a working deployment is a worse failure than a missing one —
    the tab still scores, and §1.4 Tier 2 claim 2 is that AI is additive.

    THIS ORG'S ANSWER FIRST — Story 23.5. The kill switch is per organisation, so
    `aiByOrg` says whether an Owner stopped AI spend here; `ai` is the deployment's
    answer and the fallback for a boot payload older than that story.
  */
  const ai = (org ? me.aiByOrg?.[org.id] : undefined) ?? me.ai
  const rulesOnly = ai && !ai.available ? ai : null

  return (
    <div className="flex min-h-0 flex-col gap-mdx-24 px-mdx-24 pb-mdx-48 pt-mdx-28">
      {rulesOnly ? <RulesOnlyNotice notice={rulesOnly.notice} /> : null}

      {/*
        A GRID at xl, as the export draws it: `minmax(0,1fr) minmax(0,1.08fr)`,
        which is Tailwind's `grid-cols-2` plus 8% — the 8% is not expressible
        without an arbitrary value, so the columns are equal and the bench is
        marginally narrower than drawn. Below xl they stack, and the bench
        follows the rubric rather than leading it, because it is a report ON the
        rubric.
      */}
      <div className="flex min-w-0 flex-col gap-mdx-24 xl:grid xl:grid-cols-2 xl:items-start">
        <div className="flex min-w-0 flex-col gap-mdx-20">
          <Criteria funnel={funnel} rulesOnly={!!rulesOnly} />
          <ExtractionCard funnel={funnel} rulesOnly={!!rulesOnly} />
          <Disqualifiers funnel={funnel} funnelId={funnelId} />
          <Bands funnel={funnel} />
          {/* Story 20.4 — "directly beneath the bands section", as the M2 prompt places it. */}
          <ThresholdCard funnel={funnel} />
        </div>

        <div className="flex min-w-0 flex-col gap-mdx-20">
          <Bench funnelId={funnelId} />
          <Probe funnelId={funnelId} />
          {/* Story 20.4 — the ICP card follows the probe in the export's right-hand column. */}
          <IcpCard funnel={funnel} />
        </div>
      </div>
    </div>
  )
}

/**
 * The inline prompt PRD §3.12 names — "the Qualify tab renders in rules-only
 * mode with an inline prompt".
 *
 * NOT `<Callout>`, and the difference is drawn rather than chosen. The callout
 * is a message ABOVE A FORM — a 22px round mark, `--mdx-radius-control`
 * padding, text capped at `--mdx-measure-48`, and `role="alert"`. The export
 * draws this as a full-width status band: a 26px square mark, 16/18 padding, a
 * 17px headline over a 15px detail, and `role="status"` — which is also the
 * correct semantics, because nothing here has failed and nothing is refused.
 * Reusing the callout would have been reinterpreting the reference.
 *
 * Every value is a token: `--mdx-review-tint` and `--mdx-review` are the same
 * amber pair `Callout`'s notice tone uses, and `--mdx-evidence-icon` is the
 * 26px square whose own comment says "radius 8px".
 *
 * Story 18.8.
 */
function RulesOnlyNotice({ notice }: { notice: { headline: string; detail: string } }) {
  return (
    <div
      role="status"
      className="flex items-start gap-mdx-16 rounded-mdx-control bg-mdx-review-tint px-mdx-18 py-mdx-16"
    >
      <span
        aria-hidden="true"
        className="flex size-mdx-evidence-icon flex-none items-center justify-center rounded-mdx-control-sm bg-mdx-review text-mdx-15 font-mdx-bold text-mdx-white"
      >
        !
      </span>
      <span className="flex min-w-0 flex-col gap-mdx-4">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink">
          {notice.headline}
        </span>
        <span className="text-mdx-15 text-mdx-slate">{notice.detail}</span>
      </span>
    </div>
  )
}

/* ── criteria ───────────────────────────────────────────────────────────── */

function Criteria({ funnel, rulesOnly }: { funnel: Funnel; rulesOnly: boolean }) {
  const criteria = funnel.rubric.criteria
  const total = totalWeight(criteria)
  /**
   * The extracted criterion whose points are open — one at a time, as the
   * `points` capture draws Experience: "selected, so its ranges open beneath the
   * row". Opening and closing are the screen's, never the draft's.
   */
  const [open, setOpen] = useState<string | null>(null)

  /**
   * The predicate `overWeightWarnings` asks for. `selection` lives on the block
   * and `rubric.ts` cannot import `funnel.ts` without a cycle, so the caller —
   * which has the funnel — is what answers the question.
   */
  const warnings = overWeightWarnings(criteria, (blockId) => {
    const block = funnel.blocks.find((b) => b.id === blockId)
    return block?.type === 'card_select' && block.selection === 'multi'
  })

  return (
    <Card title="Criteria" aside={CRITERIA_ASIDE}>
      {criteria.length === 0 ? (
        <div className="flex flex-col gap-mdx-6">
          <span className="text-mdx-15 font-mdx-semibold">{EMPTY_TITLE}</span>
          <span className="max-w-mdx-measure-52 text-mdx-13 text-mdx-slate">{EMPTY_BODY}</span>
        </div>
      ) : (
        <div className="flex flex-col gap-mdx-2">
          {criteria.map((criterion) => (
            <CriterionRow
              key={criterion.id}
              criterion={criterion}
              field={readsOf(funnel, criterion)}
              total={total}
              rulesOnly={rulesOnly}
              open={open === criterion.id}
              onToggle={() => setOpen((current) => (current === criterion.id ? null : criterion.id))}
            />
          ))}
        </div>
      )}

      {warnings.map((warning) => (
        /*
          NON-BLOCKING — DECISION-031. The rubric is legal and will score; it
          will just score differently from how it reads, and the operator is
          told before it fires rather than after a lead comes back wrong. Both
          numbers are named, because "some points are lost" is not actionable.

          `notice`, not `error`: nothing is broken and nothing is refused, and
          `--mdx-error` is reserved for exactly that (PRD §1.9).
        */
        <Callout key={warning.criterion_id} tone="notice">
          {warning.label} lets several answers be picked, and its options add up to{' '}
          {warning.can_sum_to} against a weight of {warning.weight}. Anything past{' '}
          {warning.weight} is clamped, so those points never reach the score.
        </Callout>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-mdx-12 border-t border-mdx-line-soft pt-mdx-16">
        <span className="text-mdx-15 font-mdx-semibold tabular-nums">
          {total} {total === 1 ? 'point' : 'points'}
        </span>
        <AddCriterion funnel={funnel} />
      </div>
    </Card>
  )
}

function CriterionRow({
  criterion,
  field,
  total,
  rulesOnly,
  open,
  onToggle,
}: {
  criterion: Criterion
  /** What the criterion reads, when it can be scored here: an extracted field, or the ICP fit. */
  field: Reads | undefined
  total: number
  rulesOnly: boolean
  open: boolean
  onToggle: () => void
}) {
  const panelId = useId()
  // A proportion of the rubric — data, in the same sense the funnels list's
  // band bar is. There is no token for "25 percent" and there should not be.
  const share = total > 0 ? Math.round((criterion.weight / total) * 100) : 0

  /*
    STORY 20.8: AN EXTRACTED CRITERION OPENS TO ITS POINTS. Only one whose field
    exists — without the field there is no type, so no shape to offer, and
    `crossReference` refuses the rubric anyway. An `answer_ref` criterion's
    points stay on the Build tab's inspector, where the answers are. Story 20.9:
    the ICP fit's criterion opens too, to ranges over its 0–100.
  */
  const opens = isRead(criterion) && field !== undefined
  /*
    POINTS THE FIELD CAN NO LONGER AWARD are said on the row, closed or open, so
    they are seen before any save can drop them (DECISION-067's shape).
  */
  const stranded =
    opens && field !== undefined ? strandedLine(strandedKeys(field, criterion.points)) : null

  /*
    RULES-ONLY TOUCHES ONLY THE CRITERIA THAT READ AN EXTRACTED FIELD, and the
    export's own note says why: "Criteria that read an extracted field show 0 of
    their weight rather than disappearing." An `answer_ref` criterion scores
    exactly as it always did — that IS §1.4 Tier 2 claim 2 — so its row must not
    move, or the screen would suggest the whole rubric had stopped.

    `transcript` is M5 and `metadata` needs no model, so `extracted` and — since
    Story 20.9 — `derived`, the ICP fit a model measures, are the whole set rather
    than "not answer_ref".
  */
  const scoresZero = rulesOnly && isRead(criterion)
  const stacked = scoresZero || open || stranded !== null

  return (
    <div
      className={[
        'rounded-mdx-control px-mdx-16 py-mdx-14 transition-colors ease-mdx-swift hover:bg-mdx-ground',
        stacked ? 'flex flex-col gap-mdx-6' : 'flex flex-wrap items-center gap-mdx-14',
      ].join(' ')}
    >
      {/*
        `contents` when nothing is annotated or open, so the default row lays
        out EXACTLY as it did before — one flex line, same gaps, same wrapping.
        The export stacks only the annotated row and the open one (`points`), and
        a wrapper that changed the common case would move four screenshots to
        describe a fifth.
      */}
      <div className={stacked ? 'flex flex-wrap items-center gap-mdx-14' : 'contents'}>
        {opens ? (
          // The name is the disclosure: the capture draws no chevron, and
          // pressing the criterion is what "selected" means there.
          <button
            type="button"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            onClick={onToggle}
            className="min-w-0 flex-1 basis-mdx-row-identity-sm cursor-pointer border-0 bg-transparent p-0 text-left text-mdx-15 font-mdx-semibold text-mdx-ink"
          >
            {criterion.label}
          </button>
        ) : (
          <span className="min-w-0 flex-1 basis-mdx-row-identity-sm text-mdx-15 font-mdx-semibold">
            {criterion.label}
          </span>
        )}

        <span
          className={[
            'inline-flex flex-none items-center rounded-mdx-pill px-mdx-12 py-mdx-5 text-mdx-13 font-mdx-semibold',
            isRead(criterion)
              ? 'bg-mdx-review-tint text-mdx-review-ink'
              : 'bg-mdx-nurture-tint text-mdx-slate',
          ].join(' ')}
        >
          {SOURCE_LABEL[criterion.source]}
        </span>

        <input
          type="number"
          aria-label={`${criterion.label} weight`}
          value={criterion.weight}
          min={1}
          onChange={(event) => {
            const raw = event.target.value.trim()
            const next = Number(raw)
            // A weight IS the criterion's maximum (`rubric.ts` fixes that), so a
            // blank or negative one makes `points_max` meaningless rather than
            // small. Refused here, and the field simply does not move. Blank is
            // checked by name, because `Number('')` is 0 — and the rubric refuses
            // a weight below 1, so 0 would be a save that fails.
            if (raw === '' || !Number.isInteger(next) || next < 1) return
            edit('Change a weight', (draft) => setWeight(draft, criterion.id, next), { coalesce: true })
          }}
          /*
            `--mdx-meta-column-sm`, the numeric slot DECISION-147 named for this
            row's 84px (Story 20.8 moved it from `--mdx-meta-column`'s 96). The
            export declares the input 72px wide in `content-box`, which renders
            near 98. DECISION-154 reads the declared 72 as the drawing, so this is
            12px wider than drawn, as DECISION-147 chose. Left at the UA default a
            number input renders near 185px.
          */
          className="w-mdx-meta-column-sm flex-none rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-10 text-mdx-15 font-mdx-semibold text-mdx-ink outline-none tabular-nums focus:border-mdx-brand"
        />

        {scoresZero ? (
          /*
            THE BAR IS REPLACED, NOT EMPTIED. A share bar drawn at 0% is a
            criterion that looks broken; the export puts a number in the same
            slot instead, so the weight is still legible and still editable
            above it — the bar's own 84px slot, `--mdx-meta-column-sm`.
          */
          <span className="w-mdx-meta-column-sm flex-none text-right text-mdx-14 font-mdx-semibold text-mdx-slate tabular-nums">
            0 of {criterion.weight}
          </span>
        ) : (
          <span
            aria-hidden="true"
            className="h-mdx-8 w-mdx-meta-column-sm flex-none overflow-hidden rounded-mdx-pill bg-mdx-line-soft"
          >
            <span className="block h-mdx-8 bg-mdx-brand" style={{ width: `${share}%` }} />
          </span>
        )}
      </div>

      {scoresZero ? (
        <span className="text-mdx-13 text-mdx-slate">{EXTRACTED_ZERO_NOTE}</span>
      ) : null}

      {stranded !== null ? <span className="text-mdx-13 text-mdx-review-ink">{stranded}</span> : null}

      {open && opens && criterion.source === 'derived' ? (
        // The capture's line under the ideal customer match: what the number is.
        <span className="text-mdx-13 text-mdx-slate">{DERIVED.describe}</span>
      ) : null}

      {open && opens && field !== undefined && (criterion.source === 'extracted' || criterion.source === 'derived') ? (
        <PointsPanel
          id={panelId}
          criterion={criterion}
          field={field}
          nothing={criterion.source === 'derived' ? NOT_MEASURED : undefined}
        />
      ) : null}
    </div>
  )
}

/**
 * A criterion is BORN in the Build tab's inspector, when a block is first
 * scored (`editor/rubric.ts`, Story 6.3). This is the second door.
 *
 * The export draws the button and not what it opens, so this offers the
 * questions an empty options map can score that carry no criterion yet, in the
 * select vocabulary the Logic tab already uses one tab over rather than a new
 * affordance invented here. Q-053 carries the design question.
 *
 * WHICH QUESTIONS is `scorableQuestions`' answer, not this component's — it
 * asks the question `crossReference` asks, because until this read it offered
 * every block with a question and a contact field was one click from a funnel
 * the autosave refused.
 */
function AddCriterion({ funnel }: { funnel: Funnel }) {
  const questions = scorableQuestions(funnel).filter(
    (block): block is Extract<Block, { question: string }> => 'question' in block,
  )
  // Story 20.3 — the door DECISION-077 said an extracted criterion did not have.
  const fields = unscoredFields(funnel)
  // Story 20.9 — the third group, while there is a description to match against.
  const fit = offersFit(funnel)
  // And, when there is none, the capture's line saying how to get one.
  const hint = hasIcpProfile(funnel) ? null : (
    <span className="max-w-mdx-measure-56 text-mdx-13 text-mdx-slate">{DERIVED.hint}</span>
  )

  const control =
    questions.length === 0 && fields.length === 0 && !fit ? (
      <span className="text-mdx-13 text-mdx-slate">
        {funnel.rubric.extraction.length > 0
          ? COMPOSED.everythingScored
          : 'Every question already feeds the score.'}
      </span>
    ) : (
      <AddCriterionSelect questions={questions} fields={fields} fit={fit} />
    )

  // The closed control above its line, as the "nothing to match against" specimen draws it.
  return hint === null ? control : <div className="flex flex-col items-start gap-mdx-8">{control}{hint}</div>
}

function AddCriterionSelect({
  questions,
  fields,
  fit,
}: {
  questions: Extract<Block, { question: string }>[]
  fields: ExtractionField[]
  fit: boolean
}) {
  return (
    <Select
      label="Add a criterion"
      value=""
      onChange={(event) => {
        // Each option says which door it opens: `question:{block id}`,
        // `field:{field key}` or `derived:icp_fit`. No id contains the separator.
        const choice = event.target.value
        const separator = choice.indexOf(':')
        if (separator < 0) return
        const door = choice.slice(0, separator)
        const ref = choice.slice(separator + 1)

        if (door === 'field') {
          edit('Add a criterion', (draft) => addExtractedCriterion(draft, ref))
          return
        }
        if (door === 'derived') {
          edit('Add a criterion', (draft) => addFitCriterion(draft))
          return
        }
        const block = questions.find((candidate) => candidate.id === ref)
        if (!block) return
        // The vocabulary's `set_criterion` (Story 27.2): the question's words, the
        // default weight, and an empty options map — its points are set on its options.
        edit('Add a criterion', (draft) => scoreQuestion(draft, block.id))
      }}
    >
      <option value="">Add a criterion</option>
      {/*
        Two groups, "visibly labelled so the source is never ambiguous" — the M2
        prompt's words. Native optgroups, so the platform draws the labels in the
        open list and a screen reader announces the group with each option.
      */}
      {questions.length > 0 ? (
        <optgroup label={PROMPTED.fromQuestion}>
          {questions.map((block) => (
            <option key={block.id} value={`question:${block.id}`}>
              {block.question}
            </option>
          ))}
        </optgroup>
      ) : null}
      {fields.length > 0 ? (
        <optgroup label={DRAWN.fromField}>
          {fields.map((field) => (
            <option key={field.field_key} value={`field:${field.field_key}`}>
              {field.label}
            </option>
          ))}
        </optgroup>
      ) : null}
      {fit ? (
        <optgroup label={DERIVED.group}>
          <option value="derived:icp_fit">{DERIVED.option}</option>
        </optgroup>
      ) : null}
    </Select>
  )
}

/* ── disqualifiers ──────────────────────────────────────────────────────── */

/**
 * Hard gates. A disqualified lead scores zero, is banded, stored and routed
 * (RULE-027) — "no score is calculated" is the export's shorthand for the
 * short-circuit, not for the lead being dropped.
 */
function Disqualifiers({ funnel, funnelId }: { funnel: Funnel; funnelId: string }) {
  const list = funnel.rubric.disqualifiers

  return (
    <Card title="Disqualifiers" aside={DISQUALIFIERS_ASIDE}>
      {list.length === 0 ? (
        <span className="text-mdx-13 text-mdx-slate">
          Nothing stops a lead yet. Every answer will be scored.
        </span>
      ) : (
        <div className="flex flex-col gap-mdx-8">
          {list.map((disqualifier) => (
            <div
              key={disqualifier.id}
              className="flex items-center gap-mdx-16 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12"
            >
              <span className="min-w-0 flex-1 text-mdx-15">
                {sentenceFor(funnel, disqualifier.when)}
              </span>
              <button
                type="button"
                onClick={() =>
                  edit('Remove a disqualifier', (draft) => {
                    draft.rubric.disqualifiers = draft.rubric.disqualifiers.filter(
                      (d) => d.id !== disqualifier.id,
                    )
                  })
                }
                className="flex-none cursor-pointer rounded-mdx-control-sm border border-mdx-line bg-mdx-white px-mdx-16 py-mdx-8 text-mdx-13 font-mdx-semibold text-mdx-error"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <AddDisqualifier funnel={funnel} />
      <ServiceArea funnel={funnel} funnelId={funnelId} />
    </Card>
  )
}

/**
 * The export draws each gate as a finished sentence — "Budget is **Under
 * $10,000**" — so this renders one, and the chooser below is what completes a
 * condition rather than an editable row that could sit half-written.
 */
function sentenceFor(funnel: Funnel, when: Condition): React.ReactNode {
  const subject = when.subject
  const value = String(when.value ?? '')

  if (subject.kind === 'metadata') {
    // `service_area` is its own metadata key (`rubric.ts`), evaluated against
    // `funnel_service_areas` from `request.cf`. Any other key is a plain
    // attribution gate and reads as itself.
    if (subject.key === 'service_area') {
      return (
        <>
          Location is <strong className="font-mdx-semibold">{SERVICE_AREA_LABEL}</strong>
        </>
      )
    }
    return (
      <>
        {subject.key} is <strong className="font-mdx-semibold">{value}</strong>
      </>
    )
  }

  const blockId = subject.kind === 'answer' ? subject.block_id : ''
  const block = funnel.blocks.find((b) => b.id === blockId)
  const name = block && 'question' in block ? block.question : (blockId || 'An answer')
  const options = block && 'options' in block ? block.options : undefined
  const readable = options?.find((option) => option.id === value)?.label ?? value

  return (
    <>
      {name} is <strong className="font-mdx-semibold">{readable}</strong>
    </>
  )
}

/**
 * Adding a gate needs a question AND an answer, and the export draws neither
 * control. Two selects in the Logic tab's own vocabulary, committing only when
 * both are chosen, so the list never holds a half-written gate. Q-053.
 */
function AddDisqualifier({ funnel }: { funnel: Funnel }) {
  const [blockId, setBlockId] = useState('')

  // `'options' in block` is how this package narrows a block — the same guard
  // `sentenceFor` above and `BuildTab` use. `Extract<Block, …>` on the Zod
  // union collapses to `never` here, so the house idiom is also the one that
  // compiles.
  const withOptions = funnel.blocks.filter(
    (block) => 'options' in block && block.options.length > 0,
  )
  const chosen = withOptions.find((b) => b.id === blockId)
  const options = chosen && 'options' in chosen ? chosen.options : []

  if (withOptions.length === 0) {
    return (
      <span className="text-mdx-13 text-mdx-slate">
        Add a question with options on the Build tab, and it can become a gate.
      </span>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-mdx-8">
      <Select
        label="Disqualify on which question"
        value={blockId}
        onChange={(event) => setBlockId(event.target.value)}
      >
        <option value="">Add a disqualifier</option>
        {withOptions.map((block) => (
          <option key={block.id} value={block.id}>
            {'question' in block ? block.question : block.id}
          </option>
        ))}
      </Select>

      {chosen && options.length > 0 ? (
        <Select
          label="Disqualify on which answer"
          value=""
          onChange={(event) => {
            const optionId = event.target.value
            if (!optionId) return
            // The inspector's Disqualify switch from the other side — the vocabulary's
            // `set_option` (Story 27.2), which names the gate and writes it once. The
            // export renders each gate as a SENTENCE from its condition, never its label.
            edit('Add a disqualifier', (draft) => setDisqualifying(draft, chosen, optionId, true))
            setBlockId('')
          }}
        >
          <option value="">Which answer?</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Select>
      ) : null}
    </div>
  )
}

/**
 * PRD §3.10, verbatim: "Service area is a country / region / city allowlist
 * evaluated from `request.cf`… **No geocoding provider is introduced**;
 * radius-based service areas are out of scope for v1."
 *
 * So three lists of names and no map, no radius and no third party. It is
 * ORG-shaped state on a funnel-shaped screen — `funnel_service_areas`, not the
 * draft — so it fetches rather than reading the schema, and a save here does
 * not go through the draft's autosave.
 */
function ServiceArea({ funnel, funnelId }: { funnel: Funnel; funnelId: string }) {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null

  const [area, setArea] = useState<{ countries: string[]; regions: string[]; cities: string[] } | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!workspaceId) return
    setError(null)
    try {
      setArea(await apiGet(`/v1/w/${workspaceId}/funnels/${funnelId}/service-area`))
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't load the service area. Check your connection and try again.",
      )
    }
  }, [workspaceId, funnelId])

  useEffect(() => {
    void load()
  }, [load])

  const gated = funnel.rubric.disqualifiers.some(
    (d) => d.when.subject.kind === 'metadata' && d.when.subject.key === 'service_area',
  )

  const save = async (next: { countries: string[]; regions: string[]; cities: string[] }) => {
    if (!workspaceId) return
    const previous = area
    setArea(next)
    setError(null)
    try {
      setArea(await apiPut(`/v1/w/${workspaceId}/funnels/${funnelId}/service-area`, next))
    } catch (e) {
      setArea(previous)
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't save the service area. Check your connection and try again.",
      )
    }
  }

  if (!gated) return null

  return (
    <div className="flex flex-col gap-mdx-10 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12">
      <span className="text-mdx-13 font-mdx-semibold">Where you work</span>
      {error ? <Callout>{error}</Callout> : null}
      {!area ? (
        <Skeleton className="h-mdx-input" />
      ) : (
        (['countries', 'regions', 'cities'] as const).map((field) => (
          <label key={field} className="flex flex-col gap-mdx-6">
            <span className="text-mdx-13 text-mdx-slate">
              {field === 'countries' ? 'Countries' : field === 'regions' ? 'Regions' : 'Cities'}
            </span>
            <input
              type="text"
              value={area[field].join(', ')}
              onChange={(event) =>
                setArea({ ...area, [field]: splitPlaces(event.target.value) })
              }
              onBlur={() => void save(area)}
              placeholder={field === 'countries' ? 'GB, IE' : ''}
              className="h-mdx-input rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 py-mdx-10 text-mdx-15 text-mdx-ink outline-none focus:border-mdx-brand"
            />
          </label>
        ))
      )}
      <span className="text-mdx-13 text-mdx-slate">
        Read from the visitor&apos;s connection. Separate entries with a comma.
      </span>
    </div>
  )
}

/** Comma-separated, trimmed, blanks dropped. The server normalises again. */
const splitPlaces = (raw: string): string[] =>
  raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)

/* ── bands ──────────────────────────────────────────────────────────────── */

/**
 * Where a score becomes a verdict.
 *
 * THE ORDER IS NOT EDITABLE, and that is RULE-002 rather than an omission: the
 * ladder runs nurture → review → hot, grey → amber → green, and it means the
 * same thing on every screen in the product. The export's own note says so, and
 * this renders it verbatim.
 *
 * BOUNDS ARE NOT VALIDATED HERE. `validateBandLadder` in
 * `packages/schema/src/rubric.ts` refuses an overlap, a gap, a ladder that does
 * not start at 0 and a top that cannot reach the rubric's ceiling — and it runs
 * at SAVE, on the server, through `funnelSchema`. A second opinion in this
 * component is the one that would drift, and it would also refuse the
 * half-finished state an operator passes through while dragging one boundary.
 */
function Bands({ funnel }: { funnel: Funnel }) {
  const bands = funnel.rubric.bands

  // The vocabulary's `set_band` (Story 27.2), which writes a bound or a name as
  // typed and leaves the ladder to the save — see above.
  const update = (id: string, label: string, fields: Parameters<typeof setBandFields>[2]) =>
    edit(label, (draft) => setBandFields(draft, id, fields), { coalesce: true })

  return (
    <Card title="Bands" aside={BANDS_ASIDE}>
      <div className="flex flex-col gap-mdx-10">
        {bands.map((band) => (
          <div key={band.id} className="flex flex-wrap items-center gap-mdx-12">
            {/*
              The swatch is the TONE, and it is not a picker. RULE-002 fixes
              what each tone means across every screen, so a colour chosen here
              would have to disagree with the funnels list, the lead drawer and
              the dashboard. `--mdx-evidence-icon` is 26px at radius 8 — the
              export's exact geometry, under a name that already means "a small
              square in a row".
            */}
            <span
              aria-hidden="true"
              className={`size-mdx-evidence-icon flex-none rounded-mdx-control-sm border border-mdx-line ${TONE_SWATCH[band.tone]}`}
            />

            <input
              type="text"
              aria-label={`${band.tone} band name`}
              value={band.label}
              onChange={(event) =>
                update(band.id, 'Rename a band', { label: event.target.value })
              }
              className="w-mdx-row-control-sm flex-none rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 py-mdx-10 text-mdx-15 font-mdx-semibold text-mdx-ink outline-none focus:border-mdx-brand"
            />

            <span className="flex flex-none items-center gap-mdx-8">
              <Bound
                label={`${band.label} minimum`}
                value={band.min}
                onChange={(next) =>
                  update(band.id, 'Move a band boundary', { min: next })
                }
              />
              <span className="text-mdx-15 text-mdx-slate">to</span>
              <Bound
                label={`${band.label} maximum`}
                value={band.max}
                onChange={(next) =>
                  update(band.id, 'Move a band boundary', { max: next })
                }
              />
            </span>

            <span className="ml-auto flex flex-none items-center gap-mdx-10">
              <span className="text-mdx-13 text-mdx-slate">Qualified</span>
              <button
                type="button"
                role="switch"
                aria-checked={band.is_qualified}
                aria-label={`${band.label} counts as qualified`}
                onClick={() =>
                  edit(
                    band.is_qualified ? 'Stop counting a band as qualified' : 'Count a band as qualified',
                    (draft) => {
                      const target = draft.rubric.bands.find((b) => b.id === band.id)
                      if (target) setBandFields(draft, band.id, { is_qualified: !target.is_qualified })
                    },
                  )
                }
                className={[
                  'flex h-mdx-24 w-mdx-40 flex-none cursor-pointer items-center rounded-mdx-pill border-0 p-mdx-3',
                  'transition-colors ease-mdx-swift',
                  band.is_qualified ? 'justify-end bg-mdx-brand' : 'justify-start bg-mdx-line',
                ].join(' ')}
              >
                <span className="size-mdx-18 rounded-mdx-pill bg-mdx-white" />
              </button>
            </span>
          </div>
        ))}
      </div>

      <p className="m-0 max-w-mdx-measure-60 text-mdx-13 text-mdx-slate">{BAND_NOTE}</p>
    </Card>
  )
}

/** One end of a band. Integer or nothing — a band boundary is a whole score. */
function Bound({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (next: number) => void
}) {
  return (
    <input
      type="number"
      aria-label={label}
      value={value}
      min={0}
      onChange={(event) => {
        const next = Number(event.target.value)
        if (Number.isInteger(next) && next >= 0) onChange(next)
      }}
      className="w-mdx-meta-column flex-none rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-10 text-mdx-15 text-mdx-ink outline-none tabular-nums focus:border-mdx-brand"
    />
  )
}

/* ── the export's card ──────────────────────────────────────────────────── */

function Card({
  title,
  aside,
  children,
}: {
  title: string
  aside: string
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{title}</span>
        <span className="text-mdx-13 text-mdx-slate">{aside}</span>
      </div>
      {children}
    </section>
  )
}
